/**
 * Risk rating vocabularies and conversions — single source of truth (AL-442).
 *
 * The same three ratings existed in the portal in four different vocabularies,
 * and converting between them took six separate functions. Five of those six
 * substituted a rating when the input was not recognised, and three substituted
 * the *least severe* one. The only function written correctly was the one that
 * writes to the database; every reader guessed. That is exactly the wrong way
 * round for a compliance value.
 *
 *   'Low Risk' | 'Med Risk' | 'High Risk'   the wire, and risk_assessments.risk_level
 *   'Low' | 'Medium' | 'High'               the risk assessment UI  (RiskRating)
 *   'LOW' | 'MEDIUM' | 'HIGH'               blotter and store       (RiskRatingUpper)
 *   1 | 2 | 3                               enhanced_profile on the C2 platform
 *
 * The primary fix landed in arcaml-c1-services commit a2d54cc, which constrained
 * risk_assessments.risk_level to the three canonical strings. So the strict
 * parsers here are unreachable for data coming from that column — which is the
 * point. They are defence in depth, and an unreachable throw is the correct
 * shape: if the assumption ever breaks, it breaks loudly instead of quietly
 * downgrading a customer's risk rating.
 *
 * Rules for anything added here:
 *  - No tolerant matching. Substring matching means 'Non-High Risk' reads as
 *    high, which is worse than failing.
 *  - No new fallback values. If a caller cannot throw, it must return null and
 *    decide explicitly, with a comment saying why.
 */

import type { EnhancedProfileLevel } from '../services/registrationApi.types';
import type { RiskRating, RiskRatingUpper } from '../types/risk-assessment.types';

/** The canonical wire vocabulary, matching the CHECK constraint on the column. */
export const WIRE_RISK_LEVELS = ['Low Risk', 'Med Risk', 'High Risk'] as const;
export type WireRiskLevel = (typeof WIRE_RISK_LEVELS)[number];

/** Raised when a value outside the canonical vocabulary reaches a parser. */
export class UnknownRiskRatingError extends Error {
	readonly received: string;

	constructor(received: string, expected: readonly string[]) {
		super(
			`Unrecognised risk rating ${JSON.stringify(received)}. ` +
			`Expected one of: ${expected.join(', ')}. ` +
			'This value cannot be displayed, because guessing would either overstate ' +
			'or understate a compliance rating.',
		);
		this.name = 'UnknownRiskRatingError';
		this.received = received;
	}
}

const WIRE_TO_UPPER: Record<WireRiskLevel, RiskRatingUpper> = {
	'Low Risk': 'LOW',
	'Med Risk': 'MEDIUM',
	'High Risk': 'HIGH',
};

const WIRE_TO_TITLE: Record<WireRiskLevel, RiskRating> = {
	'Low Risk': 'Low',
	'Med Risk': 'Medium',
	'High Risk': 'High',
};

/**
 * The write path: RiskRating → wire. A total map, so it cannot fail.
 *
 * This was already the one correct mapper (AL-442 #6) and is moved here
 * unchanged so the write vocabulary sits beside the read vocabularies rather
 * than being declared inline in a hook.
 */
export const RISK_RATING_TO_WIRE: Record<RiskRating, WireRiskLevel> = {
	High: 'High Risk',
	Medium: 'Med Risk',
	Low: 'Low Risk',
};

const UPPER_RATINGS: readonly RiskRatingUpper[] = ['LOW', 'MEDIUM', 'HIGH'];

function isWireRiskLevel(value: string): value is WireRiskLevel {
	return (WIRE_RISK_LEVELS as readonly string[]).includes(value);
}

/**
 * Wire → RiskRatingUpper. Throws on anything else.
 *
 * Replaces AL-442 #1, which returned 'LOW' for every unrecognised value, so an
 * unreadable rating became the least severe one and everything downstream then
 * behaved correctly on a wrong input.
 */
export function parseWireRiskLevelToUpper(value: string): RiskRatingUpper {
	if (!isWireRiskLevel(value)) {
		throw new UnknownRiskRatingError(value, WIRE_RISK_LEVELS);
	}
	return WIRE_TO_UPPER[value];
}

/**
 * Wire → RiskRating. Throws on anything else.
 *
 * Replaces AL-442 #5, the same silent-Low pattern on the assessment resume path.
 */
export function parseWireRiskLevelToTitle(value: string): RiskRating {
	if (!isWireRiskLevel(value)) {
		throw new UnknownRiskRatingError(value, WIRE_RISK_LEVELS);
	}
	return WIRE_TO_TITLE[value];
}

/**
 * Wire → RiskRatingUpper, returning null rather than throwing.
 *
 * For the blotter (AL-442 #3), which maps many rows at once while building a
 * list. One unreadable row must not blank the whole table, so that caller logs
 * and renders the row without a rating. Everywhere else should use the throwing
 * parser — this is not a general-purpose escape hatch.
 */
export function tryParseWireRiskLevelToUpper(value: string): RiskRatingUpper | null {
	const trimmed = value.trim();
	return isWireRiskLevel(trimmed) ? WIRE_TO_UPPER[trimmed] : null;
}

/**
 * Parse an already-upper-case rating, returning null rather than throwing.
 *
 * This exists for one caller: localStorage hydration (AL-442 #2). The database
 * constraint does not protect the browser's cache, so a stale entry written by
 * an older build is genuinely reachable. Throwing there would take the whole
 * cached list down — the read is wrapped in a try/catch that returns [] — so a
 * single bad entry would silently empty the user's history until they cleared
 * storage. Dropping the one unreadable entry is the sensible thing the caller
 * can do with a null, which is the carve-out AL-442 allows for.
 */
export function parseUpperRiskRating(value: string): RiskRatingUpper | null {
	const upper = value.trim().toUpperCase();
	return (UPPER_RATINGS as readonly string[]).includes(upper)
		? (upper as RiskRatingUpper)
		: null;
}

/**
 * RiskRatingUpper → enhanced_profile level.
 *
 * AL-442 #4, folded in here unchanged. Its input is already a constrained union,
 * so `1` is only ever reached by 'LOW' and is a correct result rather than a
 * fallback. Callers that have no assessment at all must keep defaulting to 3,
 * not to this — absence failing to the most severe rating is deliberate.
 */
export function riskRatingUpperToEnhancedProfile(
	rating: RiskRatingUpper,
): EnhancedProfileLevel {
	switch (rating) {
		case 'HIGH':
			return 3;
		case 'MEDIUM':
			return 2;
		case 'LOW':
			return 1;
	}
}
